/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Button,
  Center,
  Spinner,
  useToast,
  Divider,
  Badge,
} from "@chakra-ui/react";
import { FaCreditCard, FaSync } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../hooks/useTheme";
import PageContainer from "../../components/PageContainer";
import UnifiedCard from "../../components/UnifiedCard";

export default function PaymentMethods() {
  const { i18n } = useTranslation();
  const { isArabic, colors } = useTheme();
  const toast = useToast();

  const [storesWithPayments, setStoresWithPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');

  // Validation for method names
  const isValidName = (name) => {
    if (!name || typeof name !== "string") return false;
    const trimmed = name.trim();
    if (trimmed.length <= 1) return false;
    if (
      trimmed === "[object Object]" ||
      trimmed === "{}" ||
      trimmed === "null" ||
      trimmed === "undefined"
    )
      return false;
    if (/^[أ-ي]$/.test(trimmed) || /^[a-zA-Z]$/.test(trimmed)) return false;
    return true;
  };

  const fetchStoresWithPaymentMethods = async () => {
    setLoading(true);

    try {
      // ================================
      // 1️⃣ Get all tenants (tenantId = -1)
      // ================================
      const tenantsRes = await fetch(
        `${baseUrl}/GetAllTenantFullDetails?tenantId=-1`,
        { headers: { Accept: "application/json" } }
      );

      let tenantsData = [];
      if (tenantsRes.ok) {
        const text = await tenantsRes.text();
        try {
          tenantsData = JSON.parse(text);
        } catch {
          console.error("Invalid tenants response", text);
        }
      }

      if (!Array.isArray(tenantsData)) tenantsData = [];

      // ================================
      // 2️⃣ Get payment methods
      // ================================
      const methodsRes = await fetch(
        `${baseUrl}/PaymentMethods/GetPaymentMethods`,
        { headers: { Accept: "application/json" } }
      );

      let methodsData = [];
      if (methodsRes.ok) {
        const txt = await methodsRes.text();
        try {
          methodsData = JSON.parse(txt);
        } catch {
          console.error("Invalid payment methods", txt);
        }
      }

      if (!Array.isArray(methodsData)) methodsData = [];

      // Group payment methods per tenant
      const paymentMap = {};

      methodsData.forEach((m) => {
        const rawTenantId = m.tenantId || m.TenantId;
        const tenantId =
          rawTenantId !== undefined && rawTenantId !== null
            ? String(rawTenantId)
            : null;

        if (!tenantId) return;

        if (!paymentMap[tenantId]) paymentMap[tenantId] = [];

        const nameRaw = m.paymentMethodName || m.PaymentMethodName || "";
        const nameEngRaw =
          m.engPaymentMethodName || m.EngPaymentMethodName || "";

        const name = String(nameRaw).trim();
        const nameEng = String(nameEngRaw).trim();

        const chosenName =
          name.length >= nameEng.length ? name : nameEng || name;

        if (isValidName(chosenName)) {
          paymentMap[tenantId].push({
            paymentMethodName: chosenName,
            isActive:
              m.isActive !== undefined
                ? m.isActive
                : m.IsActive !== undefined
                  ? m.IsActive
                  : 1,
          });
        }
      });

      // ================================
      // 3️⃣ Combine tenants + methods
      // ================================
      const combined = tenantsData.map((t) => {
        const rawTenantId = t.TenantId || t.tenantId;
        const tenantId =
          rawTenantId !== undefined && rawTenantId !== null
            ? String(rawTenantId)
            : "";

        const name =
          t.TenantName ||
          t.EngTenantName ||
          t.tenantName ||
          t.engTenantName ||
          "";

        const methods = paymentMap[tenantId] || [];

        return {
          tenantId,
          tenantName: String(name),
          email: String(t.Email || t.email || ""),
          domain: String(t.SubDomain || ""),
          paymentMethods: methods,
        };
      });

      setStoresWithPayments(combined);
    } catch (err) {
      console.error("Fetch error", err);
      toast({
        title: isArabic ? "خطأ" : "Error",
        description: err.message,
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStoresWithPaymentMethods();
  }, [isArabic, baseUrl]);

  return (
    <PageContainer
      title={isArabic ? "تقرير بوابات الدفع" : "Payment Gateways Report"}
    >

      <Center mb={6}>
        <Button
          leftIcon={<FaSync />}
          onClick={fetchStoresWithPaymentMethods}
          colorScheme="purple"
        >
          {isArabic ? "تحديث" : "Refresh"}
        </Button>
      </Center>

      {loading ? (
        <Center py={10}>
          <Spinner size="xl" color={colors.mainColor} />
        </Center>
      ) : (
        <VStack align="stretch" spacing={4}>
          {storesWithPayments.map((store) => (
            <UnifiedCard key={store.tenantId}>
              <VStack align="start" spacing={3}>
                <HStack>
                  <Icon as={FaCreditCard} color={colors.mainColor} boxSize={6} />
                  <Text fontSize="lg" color={colors.textColor} fontWeight="bold">
                    🏪 {store.tenantName}
                  </Text>
                </HStack>

                <Text color={colors.subText}>
                  📧 {isArabic ? "الإيميل:" : "Email:"} {store.email}
                </Text>
                <Text color={colors.subText}>
                  🌐 {isArabic ? "الدومين:" : "Domain:"} {store.domain}
                </Text>

                <Divider />

                <Text
                  fontWeight="semibold"
                  color={colors.mainColor}
                  fontSize="md"
                >
                  {isArabic ? "طرق الدفع:" : "Payment Methods:"}
                </Text>

                {store.paymentMethods.length > 0 ? (
                  <VStack align="start" spacing={2}>
                    {store.paymentMethods.map((pm, i) => {
                      const name = pm.paymentMethodName;

                      if (!isValidName(name)) return null;

                      return (
                        <HStack
                          key={i}
                          bg={colors.hoverBg}
                          p={2}
                          borderRadius="md"
                          w="100%"
                        >
                          <Badge
                            px={3}
                            py={1}
                            borderRadius="md"
                            bg={
                              pm.isActive
                                ? colors.badgeSuccess
                                : colors.badgeError
                            }
                            color={
                              pm.isActive
                                ? colors.badgeSuccessText
                                : colors.badgeErrorText
                            }
                          >
                            {pm.isActive
                              ? isArabic
                                ? "نشط"
                                : "Active"
                              : isArabic
                                ? "غير نشط"
                                : "Inactive"}
                          </Badge>

                          <Text color={colors.textColor} fontWeight="medium">
                            💳 {name}
                          </Text>
                        </HStack>
                      );
                    })}
                  </VStack>
                ) : (
                  <Text color={colors.subText} fontStyle="italic">
                    {isArabic
                      ? "لا توجد طرق دفع"
                      : "No payment methods for this store"}
                  </Text>
                )}
              </VStack>
            </UnifiedCard>
          ))}
        </VStack>
      )}
    </PageContainer>
  );
}
